import { useEffect, useRef, useState } from 'react';
import { datToken, goi, LoiApi } from './api';
import logo from './logo.png';
import type { NguoiDung } from './kieu';

/**
 * Màn đăng nhập — một tấm NỔI giữa mặt bàn xám.
 *
 * Ấn tượng đến từ **nhịp vào màn hình**: tấm trồi lên trước, rồi từng mảnh bên
 * trong nổi theo lệch nhau một nhịp ngắn — chứ không phải cả trang bật ra cùng
 * lúc.
 */
export function DangNhap({ xong }: { xong: (nd: NguoiDung) => void }) {
  const [username, datUsername] = useState('');
  const [matKhau, datMatKhau] = useState('');
  const [loi, datLoi] = useState<string | null>(null);
  const [dangGui, datDangGui] = useState(false);

  const oUsername = useRef<HTMLInputElement>(null);
  const oMatKhau = useRef<HTMLInputElement>(null);

  /*
   * 🔴 Trình duyệt tự điền KHÔNG bắn onChange của React — và tệ hơn thế.
   *
   *    Hậu quả đã nhìn thấy thật: Chrome điền sẵn tên và mật khẩu, ô hiện đầy
   *    chữ, nhưng nút ĐĂNG NHẬP kẹt xám — nhân viên nhìn thấy đủ thông tin mà
   *    bấm không được, và không đoán ra vì sao.
   *
   *    Đã thử đọc lại `input.value` rồi nhét vào state. **Không được.** Đo tại
   *    chỗ trên Chrome: ô khớp `:-webkit-autofill` = true nhưng `.value` = ''.
   *    Chrome cố ý giấu thứ nó tự điền khỏi JavaScript cho tới khi người dùng
   *    chạm vào trang — nếu không thì bất kỳ trang nào cũng đọc trộm được mật
   *    khẩu đã lưu. Không có cách nào vòng qua, và cũng không nên có.
   *
   *    Nên đổi cách khoá nút, xem chú thích ở nút ĐĂNG NHẬP bên dưới. Hàm này
   *    giữ lại cho trường hợp người dùng CHỌN từ danh sách gợi ý: lúc đó đã có
   *    thao tác người dùng nên đọc được, và nút sáng lên ngay.
   */
  const dongBo = () => {
    const u = oUsername.current?.value ?? '';
    const m = oMatKhau.current?.value ?? '';
    if (u) datUsername((c) => (c === u ? c : u));
    if (m) datMatKhau((c) => (c === m ? c : m));
  };

  useEffect(() => {
    const h = setTimeout(dongBo, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const khiTuDien = (e: React.AnimationEvent<HTMLInputElement>) => {
    if (e.animationName === 'bat-tu-dien') dongBo();
  };

  const gui = async (e: React.FormEvent) => {
    e.preventDefault();
    if (dangGui) return;

    // Đọc thẳng từ ô, KHÔNG từ state. Bấm nút là một thao tác người dùng, nên
    // đây là thời điểm sớm nhất giá trị tự điền hiện ra với JavaScript.
    const u = (oUsername.current?.value || username).trim();
    const m = oMatKhau.current?.value || matKhau;
    if (!u || !m) {
      datLoi('Chưa nhập tên đăng nhập hoặc mật khẩu.');
      return;
    }

    datDangGui(true);
    datLoi(null);
    try {
      const d = await goi<{ token: string; nguoi_dung: NguoiDung }>('/api/auth/dang-nhap', {
        method: 'POST',
        than: { username: u, mat_khau: m },
        loi401LaSaiMatKhau: true,
      });
      datToken(d.token);
      xong(d.nguoi_dung);
    } catch (e) {
      // Backend cố ý trả CÙNG một câu cho mọi kiểu hỏng — sai mật khẩu, đã nghỉ
      // việc, cửa hàng đã đóng. Hiện đúng câu đó, đừng đoán thêm.
      datLoi(e instanceof LoiApi ? e.message : 'Không gọi được máy chủ.');
      datMatKhau('');
    } finally {
      datDangGui(false);
    }
  };

  const cham = (i: number) => ({ animationDelay: `${i * 70}ms` });

  return (
    <div
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        overflow: 'auto',
        background: 'var(--nen)',
      }}
    >
      {/*
        🔴 KHÔNG dùng gradient toả rất nhạt trải rộng để trang đỡ phẳng.

           Bản trước có một vệt hồng 5,5% trải ngang 70vw. Trên màn hình 8-bit
           nó bị **dồn thành dải** — hiện ra đúng như những vòng tròn đồng tâm
           giữa nền. Đã nhìn thấy thật.

           Ở phong cách này không cần nữa: chiều sâu của tấm nổi đã làm việc đó.
      */}
      <form
        onSubmit={gui}
        // `phong-len` chứ KHÔNG phải `troi-len`: `troi-len` chạy chính box-shadow
        // và có `fill: both`, nên giá trị cuối của nó đè lên mọi box-shadow khai
        // báo sau — kể cả dòng inline ngay dưới đây. Thẻ sẽ lặng lẽ nhận bóng
        // thường thay vì bóng to, và không có lỗi nào báo ra.
        className="noi phong-len"
        style={{ width: '100%', maxWidth: 400, padding: 40, boxShadow: 'var(--noi-to)' }}
      >
        <div className="hien-len" style={{ ...cham(0), marginBottom: 32, justifyItems: 'center' }}>
          {/* 150 = 70 × 2.1314, đúng tỉ lệ file gốc 1411×662. Xem ThanhTren.tsx. */}
          <img
            src={logo}
            alt="Color District"
            width={150}
            height={70}
            style={{ display: 'block' }}
          />
        </div>

        <div className="hien-len" style={{ ...cham(1), marginBottom: 18 }}>
          <label className="nhan-chu" htmlFor="u">
            Tên đăng nhập
          </label>
          <input
            id="u"
            ref={oUsername}
            onAnimationStart={khiTuDien}
            value={username}
            onChange={(e) => datUsername(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            placeholder="ban-hang-1"
            style={{ marginTop: 8 }}
          />
        </div>

        <div className="hien-len" style={{ ...cham(2), marginBottom: 24 }}>
          <label className="nhan-chu" htmlFor="m">
            Mật khẩu
          </label>
          <input
            id="m"
            ref={oMatKhau}
            onAnimationStart={khiTuDien}
            type="password"
            value={matKhau}
            onChange={(e) => datMatKhau(e.target.value)}
            autoComplete="current-password"
            style={{ marginTop: 8 }}
          />
        </div>

        {loi && (
          <div
            role="alert"
            className="lom-nho hien-len"
            style={{
              marginBottom: 18,
              padding: '13px 16px',
              color: 'var(--nhan-dam)',
              fontSize: 'var(--chu-nho)',
              fontWeight: 600,
            }}
          >
            {loi}
          </div>
        )}

        {/*
          🔴 Chỉ khoá khi ĐANG GỬI, không khoá theo ô rỗng.

             Khoá theo ô rỗng thì máy nào lưu mật khẩu là nút kẹt xám vĩnh viễn
             — xem chú thích ở đầu component. Ô rỗng thật thì bắt lúc bấm và
             nói thẳng ra, như vậy nhân viên luôn nhận được câu trả lời.
        */}
        <button
          type="submit"
          className="nut-chinh hien-len"
          disabled={dangGui}
          style={{ ...cham(3), width: '100%', padding: 16, fontSize: 'var(--chu-vua)' }}
        >
          {dangGui ? 'ĐANG VÀO…' : 'ĐĂNG NHẬP'}
        </button>

        <div
          className="hien-len"
          style={{
            ...cham(4),
            marginTop: 22,
            fontSize: 12,
            color: 'var(--chu-3)',
            lineHeight: 1.65,
          }}
        >
          <i>Vui lòng liên hệ cửa hàng trưởng để cài đặt lại tài khoản nếu quên mật khẩu.</i>
        </div>
      </form>
    </div>
  );
}
